import type { Company } from '../../types/company';
import type { BankTransferStatus } from './bankTransferConstants';

export type PaymentMethod = 'credit_card' | 'bank_transfer' | 'none';

export interface SubscriptionDisplayState {
  currentPlan: string;
  subscriptionStatus: string;
  paymentMethod: PaymentMethod;
  hasStripeSubscription: boolean;
  stripeCustomerId: string | null;
  bankTransferStatus: BankTransferStatus | null;
  bankTransferConfirmedAt: string | null;
  hasPendingBankTransfer: boolean;
  canChangePlan: boolean;
  canUpdateAddon: boolean;
  canSelectPaymentMethod: boolean;
  showStripeCheckout: boolean;
  showBankTransferForm: boolean;
  showBankTransferInstructions: boolean;
  showTrialAlert: boolean;
  showPastDueAlert: boolean;
  showNoSubscriptionAlert: boolean;
}

export interface CalculateDisplayStateOptions {
  hasStoredBankTransferResult?: boolean;
}

const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
  trialing: '無料登録中',
  pending_payment: '入金待ち',  // 銀行振込の入金確認待ち
  active: '有効',
  past_due: '支払い遅延',
  canceling: '解約予定',      // 期末まで利用可能
  canceled: '解約済み',
  suspended: '一時停止',      // 管理者による停止（将来機能）
};

const SUBSCRIPTION_STATUS_COLORS: Record<string, string> = {
  trialing: 'blue',
  pending_payment: 'gold',  // 銀行振込の入金待ち（注意が必要）
  active: 'green',
  past_due: 'orange',
  canceling: 'gold',      // 解約予定（注意が必要）
  canceled: 'red',
  suspended: 'volcano',   // 一時停止（管理者操作）
};

export interface BankTransferStatusUi {
  status: BankTransferStatus;
  tagColor: string;
  label: string;
  description: string;
}

const BANK_TRANSFER_STATUS_UI: Record<BankTransferStatus, BankTransferStatusUi> = {
  pending: {
    status: 'pending',
    tagColor: 'gold',
    label: '入金待ち',
    description: '銀行振込でのお申し込みを受け付けています。振込先に入金が確認されると契約が有効化されます。',
  },
  confirmed: {
    status: 'confirmed',
    tagColor: 'green',
    label: '利用可能',
    description: '銀行振込によるお支払いが有効です。請求書に記載された期日までにお振り込みください。',
  },
  rejected: {
    status: 'rejected',
    tagColor: 'red',
    label: '利用不可',
    description: '銀行振込でのお支払いは現在ご利用いただけません。サポート窓口までお問い合わせください。',
  },
};

function resolveBankTransferStatus(
  rawStatus: BankTransferStatus | null | undefined,
  options?: CalculateDisplayStateOptions,
): BankTransferStatus | null {
  if (rawStatus === 'pending') {
    return 'pending';
  }
  if (!rawStatus && options?.hasStoredBankTransferResult) {
    return 'pending';
  }
  return rawStatus ?? null;
}

export function calculateDisplayState(
  company: Company,
  options?: CalculateDisplayStateOptions,
): SubscriptionDisplayState {
  const hasStripeSubscription = Boolean(company.stripeSubscriptionId);
  const resolvedBankTransferStatus = resolveBankTransferStatus(company.bankTransferStatus, options);
  const hasPendingBankTransfer = resolvedBankTransferStatus === 'pending';

  // 支払い方法の判定ロジック
  // 1. bankTransferStatus が pending または confirmed なら銀行振込
  // 2. stripeSubscriptionId があり、bankTransferStatus がないならクレジットカード
  // 3. それ以外は未設定
  const paymentMethod: PaymentMethod = resolvedBankTransferStatus
    ? 'bank_transfer'
    : hasStripeSubscription
      ? 'credit_card'
      : 'none';

  const subscriptionStatus = company.subscriptionStatus || 'trialing';

  return {
    currentPlan: company.subscriptionPlan || 'free',
    subscriptionStatus,
    paymentMethod,
    hasStripeSubscription,
    stripeCustomerId: company.stripeCustomerId || null,
    bankTransferStatus: resolvedBankTransferStatus,
    bankTransferConfirmedAt: company.bankTransferConfirmedAt ?? null,
    hasPendingBankTransfer,
    canChangePlan: hasStripeSubscription && subscriptionStatus === 'active',  // cancelingの場合はfalse
    canUpdateAddon: hasStripeSubscription && subscriptionStatus === 'active',  // cancelingの場合はfalse
    canSelectPaymentMethod: !hasStripeSubscription && !hasPendingBankTransfer,
    showStripeCheckout: !hasStripeSubscription,
    showBankTransferForm: !hasStripeSubscription,
    showBankTransferInstructions: hasPendingBankTransfer,
    showTrialAlert: subscriptionStatus === 'trialing',
    showPastDueAlert: subscriptionStatus === 'past_due',
    showNoSubscriptionAlert: !hasStripeSubscription && !hasPendingBankTransfer,
  };
}

export function getSubscriptionStatusLabel(status?: string | null): string {
  if (!status) {
    return '不明';
  }
  return SUBSCRIPTION_STATUS_LABELS[status] ?? '不明';
}

export function getSubscriptionStatusColor(status?: string | null): string {
  if (!status) {
    return 'default';
  }
  return SUBSCRIPTION_STATUS_COLORS[status] ?? 'default';
}

export function getBankTransferStatusUi(
  status: BankTransferStatus | null | undefined,
  options?: CalculateDisplayStateOptions,
): BankTransferStatusUi | null {
  const resolved = resolveBankTransferStatus(status ?? null, options);
  if (!resolved) {
    return null;
  }
  return BANK_TRANSFER_STATUS_UI[resolved] ?? null;
}
